import { FilterIcon } from "@heroicons/react/outline";
import { SearchIcon } from "@heroicons/react/solid";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getNoteState } from "../../../components/sessions/helper";
import NoteModel from "../../../api/models/NoteModel";
import { showApiErrorMessages } from "../../../helpers";
import AppFilters from "../../../components/app-filters/AppFilters";
import ShowIf from "../../../components/show-if/ShowIf";
import TableHeaderCell from "../../../components/table-cell/TableHeaderCell";
import { useOrderBy } from "../../../hooks/useOrderBy";
import { ApiCommonParams } from "../../../api/@types/apiTypes";
import Pagination from "../../../components/pagination/Pagination";
import { useInfiniteStudentSessions } from "../../../hooks/useInfiniteStudentSessions";
import { useQuery } from "react-query";
import { indexCandidateNotes } from "../../../api/endpoints/candidate/notes";
import { indexCandidateSessions } from "../../../api/endpoints/candidate/sessions";

function CandidateNotesViewPage() {
  const LocalBasePath = "page.candidate.dashboard.notes";
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<{ [key: string]: any }>({});
  const { orderBy, getSortingDirection, handleSetSortingOrder } = useOrderBy();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchVal, setSearchVal] = useState("");
  const resetCurrentPage = () => {
    setCurrentPage(1);
  };
  let [currentPageFilter, setCurrentPageFilter] = useState<{
    [key: string]: any;
  }>({
    currentSessionsPage: 1,
  });

  const { data: notes } = useQuery(
    ["candidateNotes", currentPage, filters, orderBy, searchVal],
    () => {
      let apiParams: ApiCommonParams = {
        filters,
        orderBy,
        search: searchVal,
        pagination: { pageSize: 10, pageNumber: currentPage },
      };
      return indexCandidateNotes(apiParams);
    },
    {
      keepPreviousData: true,
      select: (response) => {
        let fetchedNotes: NoteModel[] = [];
        if (response.isSuccess) {
          fetchedNotes = NoteModel.fromApiResponse(response.data);
        } else {
          showApiErrorMessages(response);
        }
        const paginationInfo = response.pagination;
        return { data: fetchedNotes, paginationInfo };
      },
    }
  );

  const { sessions, loadMoreSessions } = useInfiniteStudentSessions(
    indexCandidateSessions
  );

  useEffect(() => {
    resetCurrentPage();
  }, [filters, orderBy]);

  useEffect(() => {
    loadMoreSessions();
  }, [currentPageFilter.currentSessionsPage]);
  const statusOptions = [
    {
      key: t("passed"),
      value: "passed",
    },
    {
      key: t("unpassed"),
      value: "unpassed",
    },
  ];
  const sessionsOptions = sessions.reverse().map((session) => ({
    key: `${t("session")} #${session.sessionNumber}`,
    value: `${session.id}`,
  }));

  const onCancelFilters = () => {
    setShowFilters(false);
    setFilters({});
  };
  return (
    <div className="w-full min-h-screen bg-white  flex flex-col  justify-between">
      <div className="px-4 md:px-6 flex flex-col justify-between  bg-gray-50 pb-6">
        <div className="py-5 text-gray-700 sm:flex sm:items-center sm:justify-between">
          <h2 className="text-2xl font-semibold">{t("recitationNotes")}</h2>
          <button
            type="button"
            data-cy="filterNotes"
            onClick={() => setShowFilters((x) => !x)}
            className="inline-flex items-center mt-4 md:mt-0 px-4 py-2 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500"
          >
            {t("filter")}
            <FilterIcon
              className="ml-3 -mr-1  rtl:mr-3 rtl:-ml-1 h-5 w-5 text-gray-500"
              aria-hidden="true"
            />
          </button>
        </div>
        <ShowIf condition={showFilters}>
          <AppFilters
            className="bg-gray-50 pb-5 flex flex-col sm:flex-row gap-4"
            filters={filters}
            setFilters={setFilters}
            onLoadMore={(key: string) =>
              setCurrentPageFilter({
                ...currentPageFilter,
                [key]: ++currentPageFilter[key],
              })
            }
            fields={[
              {
                name: "state",
                label: t("status"),
                options: statusOptions,
              },
              {
                name: "session_id",
                paginationLabel: "currentSessionsPage",
                label: t("session"),
                options: sessionsOptions,
              },
            ]}
            handleButtonClick={onCancelFilters}
          />
        </ShowIf>
        <div className="relative rounded-md shadow-sm flex justify-between gap-[18px]">
          <div className="absolute inset-y-0 rtl:right-0 lrt:left-0 rtl:pr-3 ltr:pl-3  flex items-center pointer-events-none">
            <SearchIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
          <input
            type="text"
            name="search"
            id="search"
            onChange={(e) => {
              setSearchVal(e.target.value);
            }}
            className="focus:ring-brandblue-500 focus:border-brandblue-500 block w-full rtl:pr-10 ltr:pl-10  sm:text-sm border-gray-300 rounded-md"
            placeholder={t("search")}
          />
        </div>
      </div>
      {notes?.data?.length! > 0 ? (
        <>
          <table
            data-cy="notesTable"
            className="min-w-full  hidden sm:table-cell  divide-gray-300 "
          >
            <thead className="bg-gray-100 border-y border-gray-200">
              <tr>
                <TableHeaderCell
                  className="w-[128px]  py-3 text-xs text-gray-500 font-medium text-justify"
                  sortable={true}
                  sortingDirection={getSortingDirection("session_id")}
                  onClick={handleSetSortingOrder("session_id")}
                >
                  {t("date")}
                </TableHeaderCell>
                <TableHeaderCell className="text-justify  py-3 text-xs text-gray-500 font-medium w-[80%]">
                  {t("note")}
                </TableHeaderCell>
                <TableHeaderCell
                  className="min-w-[128px] text-xs  py-3 text-gray-500 font-medium text-justify "
                  sortable={true}
                  sortingDirection={getSortingDirection("state")}
                  onClick={handleSetSortingOrder("state")}
                >
                  {t("status")}
                </TableHeaderCell>
              </tr>
            </thead>
            <tbody>
              {notes?.data.map((note, index) => {
                return (
                  <tr
                    key={`NoteInfoTable_${index}_note`}
                    className={" border-t border-gray-100"}
                  >
                    <td className={" px-3 py-3.5  lg:table-cell"}>
                      <h6 className="text-xs text-gray-500">
                        {note?.created_at?.format("DD/MM/YYYY")}
                      </h6>
                    </td>
                    <td className={"relative py-4 pl-4 sm:pl-6 pr-3 text-sm"}>
                      <div className="font-normal text-gray-500">
                        {note.note}
                      </div>
                    </td>
                    <td
                      className={
                        " px-3 py-3.5 text-xs flex flex-nowrap font-medium  text-gray-500 lg:table-cell"
                      }
                    >
                      {getNoteState(note?.status!)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="sm:hidden  w-full flex flex-col ">
            <div className="w-full bg-gray-100  px-4 py-3 border-b border-gray-200">
              <h4 className="text-gray-500 text-sm">{t("notes")}</h4>
            </div>
            <div className="w-full bg-white">
              <ul className="w-full">
                {notes?.data.map((note, index) => {
                  return (
                    <li
                      key={`NoteInfoTable_${index}_note`}
                      className={" border-b border-gray-100 pt-4 pb-1 px-4"}
                    >
                      <div
                        className={
                          "text-xs flex flex-nowrap font-medium text-gray-500"
                        }
                      >
                        {getNoteState(note?.status!)}
                      </div>
                      <div className={"relative my-3 text-sm"}>
                        <div className="font-normal text-gray-500">
                          {note.note}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="flex h-[65vh] justify-center  items-center">
            <h1>{t("noresultsfound")}</h1>
          </div>
        </>
      )}

      <div className="sticky mt-auto bottom-0">
        <Pagination
          hasNext={!!notes?.paginationInfo?.hasNext}
          hasPrev={!!notes?.paginationInfo?.hasPrev}
          goToPage={(page: number) => setCurrentPage(page)}
          currentPage={Number(notes?.paginationInfo?.currentPageNumber || 0)}
          numberOfPages={Number(notes?.paginationInfo?.pagesCount || 0)}
        />
      </div>
    </div>
  );
}
export default CandidateNotesViewPage;
